/**
 * Default Team Preferences — single source of truth for the key set seeded
 * into Appwrite Team preferences when a new client (team) is created at
 * registration. See AppwriteAuthService.completeRegistration.
 *
 * Module flags are stored as STRING "true"/"false" per the existing convention
 * (teamPreferences.service parses them back to booleans on read).
 */

/**
 * Build the full default Team-prefs payload for a newly created team.
 * `countryIso2` (when provided) resolves timezone/currency from the geo data;
 * otherwise timezone falls back to Europe/Oslo and currency to EUR.
 */
export function buildDefaultTeamPrefs(input: {
  companyWebsite?: string | null;
  ownerEmail: string;
  language: string;
  countryCode?: string | null;
  countryName?: string | null;
  stateName?: string | null;
  timezone?: string | null;
  currency?: string | null;
}): Record<string, any> {
  // NOTE: empty values are the STRING "null" (storage form). JSON nulls
  // freeze the Appwrite Console pref editor — see services/appwritePrefsSchema.ts.
  const NULL = 'null';
  return {
    company_website: input.companyWebsite || NULL,

    // Localization
    language: input.language,
    timezone: input.timezone || 'Europe/Oslo',
    currency: input.currency || 'EUR',
    // country = FULL NAME from geo_countries; the ISO2 code lives in country_code.
    country: input.countryName || NULL,
    country_code: input.countryCode || NULL,
    state: input.stateName || NULL,

    // Billing + access control (status: active | blocked | invited)
    billing_email: input.ownerEmail,
    status: 'active',
    partner: 'false',

    // Module activation flags — Leads ON by default, everything else OFF.
    mod_leads: 'true',
    mod_outreach: 'false',
    mod_sales: 'false',
    mod_workers: 'false',
    mod_nexus: 'false',
    mod_chat: 'false',
    mod_goals: 'false',

    // External-account placeholders — filled when the client connects each provider.
    lvl1_id: NULL,
    stripe_id: NULL,
    clickup_id: NULL, // internal CRM id assigned by Brainiacs
    seller_ids: NULL,
    instantly_org: '5c0c2c07-9ffd-40bc-91ca-2ac366da62fc',
    Instantly_domains: NULL,
    unipile_whatsapp_ids: NULL,
    unipile_linkedin_ids: NULL,
    twilio_account_id: NULL,
    twilio_phones_ids: NULL,
    elevenlabs_phone_ids: NULL,

    // Branding placeholders (team-branding-manager writes real values).
    brand_primary_colour: NULL,
    logo_url: NULL,
  };
}
